import { makeAutoObservable, runInAction } from "mobx";
import { RootStore, ServiceStore } from "@intrasync/stores";
import { ProductionOrder } from "../../ProductionOrdersList.Store";

// Form data interface based on the production order schema
interface ProductionOrderFormData {
  orderNumber: string;
  productionType:
    | "Precast Panels"
    | "Structural Elements"
    | "Custom Components"
    | "Architectural Elements"
    | "Infrastructure"
    | "Other";
  status:
    | "Planned"
    | "Scheduled"
    | "In Production"
    | "Quality Check"
    | "Completed"
    | "On Hold"
    | "Cancelled";
  priority: "Low" | "Medium" | "High" | "Critical";
  projectId?: number;
  bidId?: number;
  accountId?: number;
  productDescription?: string;
  quantity?: number;
  unitOfMeasure?: string;
  estimatedHours?: number;
  actualHours?: number;
  plannedStartDate?: string;
  plannedEndDate?: string;
  actualStartDate?: string;
  actualEndDate?: string;
  estimatedCost?: number;
  actualCost?: number;
  materialCost?: number;
  laborCost?: number;
  percentComplete?: number;
  qualityScore?: number;
  assignedTo?: number;
  supervisorId?: number;
  notes?: string;
  specialInstructions?: string;
}

interface ValidationErrors {
  orderNumber?: string;
  productionType?: string;
  status?: string;
  priority?: string;
  projectId?: string;
  quantity?: string;
  estimatedHours?: string;
  estimatedCost?: string;
  plannedStartDate?: string;
  plannedEndDate?: string;
}

export class ProductionOrderFormStore {
  rootStore: RootStore;

  // Observable state
  formData: ProductionOrderFormData = {
    orderNumber: "",
    productionType: "Precast Panels",
    status: "Planned",
    priority: "Medium",
  };

  originalData: ProductionOrderFormData | null = null;
  isEditing = false;
  productionOrderId: number | null = null;
  loading = false;
  saving = false;
  error: string | null = null;
  validationErrors: ValidationErrors = {};
  isDirty = false;

  // Related data loading state
  loadingRelatedData = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // Service store access
  get productionOrdersStore(): ServiceStore {
    return this.rootStore.productionOrdersStore as ServiceStore;
  }

  get jobStore(): ServiceStore {
    return this.rootStore.jobStore as ServiceStore;
  }

  get accountsStore(): ServiceStore {
    return this.rootStore.accountsStore as ServiceStore;
  }

  get bidsStore(): ServiceStore | undefined {
    return this.rootStore.bidsStore as ServiceStore | undefined;
  }

  // Form validation
  get isValid(): boolean {
    this.validateForm();
    return Object.keys(this.validationErrors).length === 0;
  }

  get hasChanges(): boolean {
    if (!this.originalData) return true;
    return JSON.stringify(this.formData) !== JSON.stringify(this.originalData);
  }

  // Form management methods
  async initializeForm(productionOrderId?: string) {
    // Prevent multiple concurrent initialization calls
    if (this.loading) {
      return;
    }

    this.loading = true;
    this.error = null;

    try {
      await this.loadRelatedData();

      if (productionOrderId) {
        const id = parseInt(productionOrderId);
        if (!isNaN(id)) {
          await this.loadProductionOrder(id);
          this.isEditing = true;
          this.productionOrderId = id;
        }
      } else {
        this.resetToDefaults();
        this.isEditing = false;
        this.productionOrderId = null;
      }

      runInAction(() => {
        this.originalData = { ...this.formData };
        this.isDirty = false;
      });
    } catch (error) {
      this.error =
        error instanceof Error ? error.message : "Failed to initialize form";
      console.error("Failed to initialize production order form:", error);
    } finally {
      this.loading = false;
    }
  }

  private async loadProductionOrder(id: number) {
    try {
      const order = (await this.productionOrdersStore.get(
        id
      )) as unknown as ProductionOrder;
      if (order) {
        runInAction(() => {
          this.formData = {
            orderNumber: order.orderNumber,
            productionType: order.productionType || "Precast Panels",
            status: order.status || "Planned",
            priority: order.priority || "Medium",
            projectId: order.projectId,
            bidId: order.bidId,
            accountId: order.accountId,
            productDescription: order.productDescription,
            quantity: order.quantity,
            unitOfMeasure: order.unitOfMeasure,
            estimatedHours: order.estimatedHours,
            actualHours: order.actualHours,
            plannedStartDate: order.plannedStartDate,
            plannedEndDate: order.plannedEndDate,
            actualStartDate: order.actualStartDate,
            actualEndDate: order.actualEndDate,
            estimatedCost: order.estimatedCost,
            actualCost: order.actualCost,
            materialCost: order.materialCost,
            laborCost: order.laborCost,
            percentComplete: order.percentComplete,
            qualityScore: order.qualityScore,
            assignedTo: order.assignedTo,
            supervisorId: order.supervisorId,
            notes: order.notes,
            specialInstructions: order.specialInstructions,
          };
        });
      }
    } catch (error) {
      throw new Error(`Failed to load production order: ${error}`);
    }
  }

  private async loadRelatedData() {
    this.loadingRelatedData = true;
    try {
      // Load projects
      await this.jobStore.find();

      // Load accounts
      await this.accountsStore.find();

      // Load bids if available
      if (this.bidsStore) {
        await this.bidsStore.find();
      }
    } catch (error) {
      console.error("Failed to load related data:", error);
    } finally {
      runInAction(() => {
        this.loadingRelatedData = false;
      });
    }
  }

  private resetToDefaults() {
    this.formData = {
      orderNumber: "",
      productionType: "Precast Panels",
      status: "Planned",
      priority: "Medium",
    };
  }

  // Form field updates
  updateField<K extends keyof ProductionOrderFormData>(
    field: K,
    value: ProductionOrderFormData[K]
  ) {
    this.formData[field] = value;
    this.isDirty = true;
    this.clearFieldError(field as string);
  }

  private clearFieldError(field: string) {
    if (this.validationErrors[field as keyof ValidationErrors]) {
      delete this.validationErrors[field as keyof ValidationErrors];
    }
  }

  // Validation
  private validateForm() {
    const errors: ValidationErrors = {};

    if (!this.formData.orderNumber?.trim()) {
      errors.orderNumber = "Order number is required";
    }

    if (!this.formData.productionType?.trim()) {
      errors.productionType = "Production type is required";
    }

    if (!this.formData.status?.trim()) {
      errors.status = "Status is required";
    }

    if (!this.formData.priority?.trim()) {
      errors.priority = "Priority is required";
    }

    if (this.formData.quantity && this.formData.quantity < 0) {
      errors.quantity = "Quantity cannot be negative";
    }

    if (this.formData.estimatedHours && this.formData.estimatedHours < 0) {
      errors.estimatedHours = "Estimated hours cannot be negative";
    }

    if (this.formData.estimatedCost && this.formData.estimatedCost < 0) {
      errors.estimatedCost = "Estimated cost cannot be negative";
    }

    if (this.formData.plannedStartDate && this.formData.plannedEndDate) {
      const startDate = new Date(this.formData.plannedStartDate);
      const endDate = new Date(this.formData.plannedEndDate);
      if (endDate <= startDate) {
        errors.plannedEndDate = "End date must be after start date";
      }
    }

    runInAction(() => {
      this.validationErrors = errors;
    });
  }

  // Form actions
  async saveProductionOrder(): Promise<boolean> {
    if (!this.isValid) {
      return false;
    }

    this.saving = true;
    this.error = null;

    try {
      if (this.isEditing && this.productionOrderId) {
        await this.productionOrdersStore.patch(
          this.productionOrderId,
          this.formData
        );
      } else {
        await this.productionOrdersStore.create(this.formData);
      }

      runInAction(() => {
        this.isDirty = false;
        this.originalData = { ...this.formData };
      });

      return true;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error
            ? error.message
            : "Failed to save production order";
        console.error("Failed to save production order:", error);
      });
      return false;
    } finally {
      runInAction(() => {
        this.saving = false;
      });
    }
  }

  resetForm() {
    this.formData = {
      orderNumber: "",
      productionType: "Precast Panels",
      status: "Planned",
      priority: "Medium",
    };
    this.originalData = null;
    this.isEditing = false;
    this.productionOrderId = null;
    this.validationErrors = {};
    this.isDirty = false;
    this.error = null;
  }

  // Utility methods
  get statusOptions() {
    return [
      "Planned",
      "Scheduled",
      "In Production",
      "Quality Check",
      "Completed",
      "On Hold",
      "Cancelled",
    ];
  }

  get productionTypeOptions() {
    return [
      "Precast Panels",
      "Structural Elements",
      "Custom Components",
      "Architectural Elements",
      "Infrastructure",
      "Other",
    ];
  }

  get priorityOptions() {
    return ["Low", "Medium", "High", "Critical"];
  }

  // Computed values for related data
  get availableProjects() {
    return this.jobStore.entities || [];
  }

  get availableAccounts() {
    return this.accountsStore.entities || [];
  }

  get availableBids() {
    return this.bidsStore?.entities || [];
  }

  // Cleanup
  dispose() {
    // Clean up any subscriptions or timers if needed
  }
}

export default ProductionOrderFormStore;
